import LinkInternal from 'components/LinkInternal';
import MediaGroup from 'components/MediaGroup';
import MediaLink from 'components/MediaLink';
import VideoInline from 'components/VideoInline';
import { addSearchParamsToUrl, isAnimatedLinkType } from 'helper/js/helper';
import { useAnimationsEnabled } from 'providers/Animation';
import { memo, useEffect, useRef } from 'react';

type MediaGroupsProps = {
  links: ALink[];
  videos: AVideos;
  isPromotion: boolean;
  promotionId?: string;
  promotionTitle?: string;
  currentVisible: number;
  productsInWishList: string[];
  ar?: string;
  firstInteractiveElementId: string | null;
};

const getMappedType = (
  displayType: ALinkDisplayTypes,
  isAnimatedLinkWithProductLink: boolean,
  isAnimatedLinkWithStandardLink: boolean,
) => {
  const MAPPING: IKeyValueData = {
    standard: ['link-media'],
    url: ['link-media'],
    internal: ['internal'],
    product: ['product-sidebar'],
    productnodetails: ['product-shop'],
    recipe: ['recipe'],
    video: ['video'],
    text: ['text'],
    textlong: ['text'],
  };

  if (isAnimatedLinkWithProductLink) {
    return MAPPING['product'];
  } else if (isAnimatedLinkWithStandardLink) {
    return MAPPING['standard'];
  }

  return MAPPING[displayType] || [];
};

const MediaGroups = ({
  links,
  currentVisible,
  productsInWishList,
  isPromotion = false,
  promotionId,
  promotionTitle,
  ar,
  videos = {},
  firstInteractiveElementId,
}: MediaGroupsProps) => {
  const { areAnimationsEnabled } = useAnimationsEnabled();
  const firstInteractiveRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      {links.map((link: ALink) => {
        const {
          displayType,
          id,
          details,
          text,
          title,
          icon,
          pageNumber,
          top,
          left,
          width,
          height,
        } = link;

        const isAnimatedLink = isAnimatedLinkType(displayType);

        // don't render anything if animations are disabled
        if (isAnimatedLink && !areAnimationsEnabled) {
          return null;
        }

        const destination =
          displayType === 'rendercloud_animation'
            ? link.rclAnimationDetails?.destination
            : details?.destination;

        const isVideo = displayType === 'inline_video';
        const isAnimatedLinkWithProductLink =
          isAnimatedLink && destination?.displayType === 'product';
        const isAnimatedLinkWithStandardLink =
          isAnimatedLink && destination?.displayType === 'standard';
        const animationUrl = isAnimatedLink ? link.url : undefined;
        const url = isAnimatedLinkWithStandardLink ? destination.url : link.url;
        const types = getMappedType(
          displayType,
          isAnimatedLinkWithProductLink,
          isAnimatedLinkWithStandardLink,
        );
        const productData: AProductData | undefined = isAnimatedLinkWithProductLink
          ? destination.details
          : undefined;

        const shouldFocusThisElement = id === firstInteractiveElementId;

        const isProductLinkForMediaLink =
          displayType === 'product' ||
          displayType === 'productnodetails' ||
          isAnimatedLinkWithProductLink;

        return (
          <MediaGroup
            key={id}
            iconsInGroup={types.length}
            currentVisible={currentVisible}
            animationUrl={animationUrl}
            displayType={displayType}
            top={top}
            left={left}
            width={width}
            hasMediaLinks={types.length > 0}
            height={height}
            rclAnimationDetails={link.rclAnimationDetails}>
            {({ calculatedWidth, relation }: { calculatedWidth: number; relation: number }) => {
              if (isVideo) {
                return (
                  <VideoInline
                    video={videos[id]}
                    relation={relation}
                    width={calculatedWidth}
                    ref={shouldFocusThisElement ? firstInteractiveRef : null}
                  />
                );
              }

              if (displayType === 'internal' && pageNumber) {
                return (
                  <LinkInternal
                    page={pageNumber}
                    ref={shouldFocusThisElement ? firstInteractiveRef : null}
                  />
                );
              }

              const params = {
                type: displayType,
                link: url ? addSearchParamsToUrl(url, { ar }) : '',
                id,
                text,
                icon,
                relation,
                groupWidth: calculatedWidth,
                title,
                isPromotion,
                promotionId,
                promotionTitle,
              };

              return types.map((mType) => (
                <MediaLink
                  key={`${id}${mType}`}
                  {...params}
                  type={mType}
                  flipped={productsInWishList.indexOf(id) > -1}
                  product={productData}
                  isAnimatedLink={isAnimatedLink}
                  ref={shouldFocusThisElement ? firstInteractiveRef : null}
                  isProductLink={isProductLinkForMediaLink}
                />
              ));
            }}
          </MediaGroup>
        );
      })}
      <FocusOnMount targetRef={firstInteractiveRef} />
    </>
  );
};

export const FocusOnMount: React.FC<{ targetRef: React.RefObject<HTMLElement> }> = ({
  targetRef,
}) => {
  useEffect(() => {
    // delay focus to ensure element is rendered and interactive
    const timer = setTimeout(() => {
      if (targetRef.current) {
        targetRef.current.focus({ preventScroll: true });
      }
    }, 450);

    return () => clearTimeout(timer);
  }, []);
  return null;
};

export default memo(MediaGroups);
